/**
 * 이상한 방 - 튜닝 상수 단일 출처
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.4절(정답 방) · 7절
 *
 * ## 왜 한 파일인가
 *
 * 컨셉시트 7절은 아래 값들을 **"v0.1에서 논쟁 금지 - 플레이테스트로만 답이 나오는 수치"**로 못박았다.
 * 즉 이 파일의 숫자는 전부 **틀렸다고 가정하고 시작하는 값**이며, 존재 목적은 "옳음"이 아니라
 * **빠르게 바꿔 보는 것**이다.
 *
 * 그래서 두 가지를 강제한다:
 *  1. **흩어지지 않는다.** 탄환 수가 HUD에도 리듀서에도 박혀 있으면 조정할 때마다 둘이 갈라지고,
 *     갈라진 순간 "6발이 재밌나 8발이 재밌나"라는 질문 자체가 성립하지 않는다.
 *  2. **재빌드 없이 바뀐다.** URL 쿼리로 덮어쓴다(`parseTuningOverrides`). 한 판이 최대 6분인데
 *     수치 하나 바꿀 때마다 빌드를 기다리면 하루에 돌릴 수 있는 판수가 한 자릿수로 떨어진다.
 *
 * ## 이 파일이 답을 정하지 않는 것
 *
 * `allowedVerbs`는 A/B 비교 대상이다(상태모델 5.3절). "추가/제거만" 3판 vs "전체" 3판을
 * 같은 사람이 연달아 돌려 보는 것이 이 필드의 유일한 존재 이유이므로,
 * 런타임에 바꿀 수 없으면 필드를 둔 의미가 없다.
 */

import { VERBS, type Verb } from '../core/types'
import { MAX_MAGAZINE_COUNT } from './magazines'

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 튜닝 값의 실제 형태.
 *
 * 리터럴 타입(`as const`)을 쓰지 않는 이유: `changeCount: 12`가 리터럴이 되면
 * `Partial<Tuning>`에 12 이외의 값을 넣을 수 없게 되어 덮어쓰기 자체가 불가능해진다.
 */
type TuningValues = {
  /** 라운드당 변화 수. 5.1절에 의해 "건드린 오브젝트 수"와 1:1이다. */
  changeCount: number
  /** 시작 탄환. **탄환 상한이기도 하다**(`gameState.ammoCapacity`). */
  startAmmo: number
  /** 탄창 하나가 채우는 탄환. 상한(`gameState.ammoCapacity`)에 걸리면 실제 회복은 이보다 적다. */
  ammoPerMagazine: number
  /** 한 라운드에서 쓸 수 있는 탄창 수. 0이면 그 판은 탄창을 못 쓴다. */
  maxMagazinesPerRound: number
  /** 외우기 페이즈 길이(초). */
  watchSeconds: number
  /** 수색 페이즈 상한(초). */
  huntSeconds: number
  /** 리빌에서 미발견 변화 하나를 보여 주는 시간(초). */
  revealSecondsPerChange: number
  /** 정답 방 재입장 쿨타임(초). */
  originCooldownSeconds: number
  /** 정답 방 최대 체류(초). */
  originStaySeconds: number
  /** 원본↔변경본 전환 디졸브(초). */
  warpDissolveSeconds: number
  /** 이 잔탄 이하로 떨어지는 순간 경고 연출을 트리거한다. */
  lowAmmoWarningAt: number
  /** 이번 라운드에 허용되는 변화 동사(상태모델 5.3절). */
  allowedVerbs: readonly Verb[]
  /**
   * 걸기(M7)를 선언할 수 있는 최소 잔탄. 이 아래에서는 거부다 - 절박한 올인을 막는다(`game/raise.ts`).
   * 싱글·매치 둘 다 읽는다(같은 `canRaise`).
   */
  raiseMinAmmo: number
  /** 걸기 뒤 빗나감 한 발의 값(배수). 기본 2 - "빗맞는 값이 두 배가 되므로 더 신중해진다"(로드맵 3.5절). */
  raiseMissCost: number

  /* --- 매치(여럿이) 전용. 싱글 리듀서는 아래 값을 읽지 않는다 --- */

  /** 꾸미기 페이즈 길이(초). 컨셉시트 3절. `humanDecorate`가 켜질 때만 흐른다. */
  decorateSeconds: number
  /** 바꾸기 페이즈 길이(초). 아무도 손댈 사람이 없는(전원 이웃·전원 끊김) 라운드는 이 값과 무관하게 0이다. */
  changeSeconds: number
  /**
   * 여럿이 판의 **인당** 이상한 곳 수(로드맵 M11). 라운드 전체는 `좌석 수 × 이 값`이다.
   * `changeCount`(싱글의 라운드 총량)와 갈라 둔 이유가 규칙이다 - 여럿이의 총량은 사람 수의 함수이고,
   * 한 사람이 만들 양은 60초 안에 손이 낼 수 있는 양(M4 플레이테스트: 탭당 2.2초까지 12개)이라 서로 다른 축이다.
   */
  changesPerPlayer: number
  /**
   * 꾸미기(M10)의 **인당 배치 수**. `humanDecorate`가 켜진 판에서만 흐르고, 방의 규칙으로 실려 간다
   * (`matchState.roomConfigFrom` → `RoomConfig.placementsPerPlayer` → `core/verbs.applyAdd`가 집행).
   */
  placementsPerPlayer: number
  /** 체인저 점수 - 미발견 변화 하나의 값. 컨셉시트 4.5절. */
  changerPointsPerUnfound: number
  /** `sniped` 피드백을 낼 창(초). 먼저 찾힌 것을 이 안에 쏘면 "OO이 N초 먼저"가 간다. */
  snipedWindowSeconds: number
  /** 원래 방 차례 예약 유예(초). 줄 머리가 이 안에 누르지 않으면 다음 사람에게 넘어간다. */
  doorReserveSeconds: number
  /**
   * 재접속 유예(초, M6). 끊긴 사람의 토큰이 이 동안 살아 있어 같은 좌석(탄환·발견 그대로)으로 돌아온다. 좌석 자체는 유예와
   * 무관하게 라운드 끝까지 남는다(`matchState.onLeave` - 봇이 대체하지 않는다) - 이 값이 정하는 것은 **돌아올 수 있는 시간**이다.
   * 서버(`server/src/room.ts`)만 읽는다. 루프백에는 끊김이 없다.
   */
  reconnectGraceSeconds: number
  /** CPU 술래의 판단 간격(초). 상태모델 9.1절의 N. */
  botIntervalSeconds: number
  /** CPU 술래가 판단 한 번에 정답을 찾을 확률. 9.1절의 p. */
  botFindChance: number
  /** CPU 술래가 판단 한 번에 오답을 낼 확률. 9.1절의 q. */
  botMissChance: number
  /** CPU 술래가 판단 한 번에 원래 방을 요청할 확률. */
  botOriginChance: number
}

/** 모든 튜닝 값. 다른 모듈은 이 타입만 받고 개별 숫자를 직접 import 하지 않는다. */
export type Tuning = Readonly<TuningValues>

/* --- 기본값 ----------------------------------------------------------------- */

/**
 * 기본 튜닝. **전부 플레이테스트 대상이다**(컨셉시트 7절).
 * 주석의 "7절 항목"은 그 값이 논쟁이 아니라 관측으로 정해져야 함을 뜻한다.
 */
export const TUNING: Tuning = {
  /**
   * 7절 항목("변화 10개 고정 vs 오브젝트 수 비례"). 컨셉시트는 10개였으나 방이 391개짜리
   * 아파트로 커지면서 12개로 올렸다 - 밀도가 오르면 같은 개수도 체감 난이도가 오른다.
   */
  changeCount: 12,

  /**
   * 7절 항목(원래 "시작 탄환 6발" → 12발 → **16발**).
   *
   * 16으로 올린 이유는 **라운드 안의 보급이 통째로 사라졌기 때문**이다. 직전까지는
   * 12발로 시작해 방에 놓인 아이템으로 최대 18발을 더 주울 수 있었다(총 예산 30발).
   * 그 아이템이 계정 단위 탄창으로 빠져나가면서, 라운드 안에서 확실히 손에 쥐는 탄환은
   * `startAmmo` 하나만 남았다. 12를 그대로 두면 개편이 "보급 방식 변경"이 아니라
   * **예산 60% 삭감**이 되고, 개발자가 두 번 "너무 어렵다"고 한 판이 더 어려워진다.
   *
   * 16은 변화 12개 기준으로 **오답 15번까지 버티는** 값이다. 탄창은 이 위에 얹히는 보조일 뿐,
   * 이 값 하나로 클리어 조건을 채울 수 있어야 한다는 것이 밸런스의 대전제다(`levels.ts`).
   *
   * **동시에 탄환 상한**이기도 하다(`gameState.ammoCapacity`) - 탄창을 써도 이 위로는 안 쌓인다.
   * 상한이 없으면 최적 전략이 "탄창부터 다 털어 넣기"로 수렴해 잔탄 긴장이 사라진다.
   */
  startAmmo: 16,

  /**
   * 7절 갱신 항목. 탄창 하나가 채우는 탄환(`magazines.ts` - 계정 단위 자원).
   *
   * 시작 탄환의 절반보다 조금 적게 잡았다. **한 판을 처음부터 다시 시작하는 느낌이면 안 된다** -
   * 그러면 탄창이 "실수를 만회하는 보조"가 아니라 "판을 통째로 사는 물건"이 되고,
   * 나중에 붙을 결제가 곧바로 페이투윈이 된다. 6발은 "오답 여섯 번을 되돌린다"는 크기다.
   */
  ammoPerMagazine: 6,
  /**
   * 7절 갱신 항목. 한 라운드에서 쓸 수 있는 탄창 수.
   *
   * **상한을 두는 것이 이 값의 존재 이유다.** 없으면 탄창을 많이 가진 사람은 라운드를 무한히
   * 연장할 수 있고, 그 순간 "잔탄이 마르면 끝난다"는 규칙이 돈으로 사라진다.
   * 2개면 최대 12발을 더 쓰는 셈이라 예산이 16 + 12 = 28발이 되는데,
   * 이는 아이템 시절의 이론상 최대치(30발)와 거의 같다 - **개편으로 천장이 올라가지는 않았다.**
   */
  maxMagazinesPerRound: 2,

  /**
   * 외우기 1분(컨셉시트 3절). **줄이면 수색이 성립하지 않는다** - 기준 기억이 없으면
   * 변화를 찾는 게 아니라 방을 처음 보는 것이 되고, 그건 다른 게임이다.
   */
  watchSeconds: 60,
  /** 7절 항목("수색 5분"). 12개를 다 찾으면 조기 종료하므로 실제 체감은 이보다 짧다. */
  huntSeconds: 300,
  /**
   * 리빌 1건당 시간. 컨셉시트 3절의 "~30초"를 12개로 나눈 값(2.5초)이다.
   * 라운드의 클라이맥스이므로 너무 빠르면 "저걸 못 봤다고???"가 나올 틈이 없다.
   */
  revealSecondsPerChange: 2.5,

  /**
   * 7절 항목(원래 "재입장 쿨 30초"). **개발자가 5초로 낮췄다. 대가를 알고 내린 결정이다.**
   *
   * 이 값은 여전히 깜빡비교(원본↔변경본 고속 토글)의 유일한 방어선이다 - 워프가 같은 위치·시점을
   * 유지하므로, 0에 가까우면 바뀐 부분이 움직임처럼 튀어나온다(motion transient).
   * 5초는 그 잔상이 사라지기에는 충분하지만 단기 기억은 선명하게 남는 길이라,
   * **게임의 성격이 "기억"에서 "비교"로 바뀐다.** 30초일 때는 방을 외워야 했고 5초면 대조하면 된다.
   *
   * 성격이 바뀌는 것을 감수한 이유는 난이도다. 30초에서는 원본 방 왕복 한 번의 대가가 너무 커서
   * 사실상 봉인된 기능이었고, 봉인된 기능은 이중 역할이고 뭐고 존재하지 않는 것과 같다.
   *
   * **계속 튜닝 대상이다.** 3초가 나은지 10초여도 충분한지는 `?cooldown=`으로 재 봐야 한다.
   */
  originCooldownSeconds: 5,
  /** 7절 항목("체류 20초"). 상한이 없으면 종료 직전 정답 방 점거가 최적 전략이 된다. */
  originStaySeconds: 20,
  /**
   * 전환 디졸브. **즉시 컷이면 안 된다** - 컷은 방향감각을 잃게 할 뿐 아니라
   * 그 자체가 motion transient가 되어 쿨타임이 막으려던 것을 그대로 열어 준다.
   */
  warpDissolveSeconds: 0.4,

  /** 컨셉시트 4.3절("잔탄 2발 경고 연출"). 귀책 인지 UI의 트리거 지점. */
  lowAmmoWarningAt: 2,

  /**
   * 7절 갱신 항목(상태모델 10.1절: "허용 동사 조합 - 추가/제거만 vs 전체의 A/B").
   * 기본값은 전체지만, `?verbs=add,remove`로 좁혀 같은 방을 다른 규칙으로 돌려 보는 것이 목적이다.
   */
  allowedVerbs: VERBS,

  /**
   * 로드맵 3.5절 "잔탄 ≥ 4일 때만". 3 이하는 "이미 진 판에 남은 것을 전부 얹는" 선택이라 걸기가 아니라 절박함이고,
   * 그것을 허용하면 걸기의 뜻("관찰에 확신이 들 때 판을 키운다")이 사라진다. 시작 탄환 16의 4분의 1이다. 플레이테스트 대상.
   */
  raiseMinAmmo: 4,
  /**
   * 로드맵 3.5절 "빗나감 2발". 1이면 걸기에 대가가 없어 언제나 거는 것이 최적이 되고, 3 이상이면 한 번 빗나간 뒤
   * 잔탄이 곧장 마른다. 발견 점수 ×2(`raise.ts`의 `RAISE_MULTIPLIER`)와 짝이라 둘을 함께 재야 한다. 플레이테스트 대상.
   */
  raiseMissCost: 2,

  /*
   * --- 매치 전용 값. 전부 7절 항목이라 여기 둔다 -------------------------------------
   *
   * 싱글 리듀서(`gameState.ts`)는 아래 값을 하나도 읽지 않는다. 그래도 `Tuning` 하나에 두는 이유는
   * URL 덮어쓰기 표가 하나여야 하기 때문이다 - 봇 판을 `?botFind=0.5`로 재빌드 없이 재는 것이
   * 이 값들의 존재 이유이고, 표가 둘이면 어느 쪽이 먹었는지부터 헷갈린다.
   */

  /** 컨셉시트 3절 "꾸미기 1분". M10 전에는 `humanDecorate`가 항상 거짓이라 흐르지 않는다. */
  decorateSeconds: 60,
  /**
   * 컨셉시트 3절 "바꾸기 1분". "12개/60초가 사람에게 되는가"(7절)가 M4의 진짜 산출물이라
   * 이 값이 그 실험의 손잡이다. **CPU 체인저 라운드는 0이다** - 사람이 만들 시간이 필요할 때만
   * 기다린다. 봇 판의 반복 속도가 개발 속도다.
   */
  changeSeconds: 60,
  /**
   * 인당 3개. M4가 잰 것은 "한 사람이 60초에 12개"였고 그 답은 예 였는데(탭당 2.2회), M11에서는 **전원이 동시에**
   * 만들므로 한 사람의 몫이 그만큼 줄어야 한다. 4석 판이 12개(싱글과 같은 총량)가 되는 수가 3이라 거기서 잡았다 -
   * 8석이면 24개이고 방도 그만큼 넓다(구획이 여덟이라 구획당 밀도는 같다). 플레이테스트 대상이다.
   */
  changesPerPlayer: 3,
  /**
   * 인당 8개(로드맵 M10 "인당 8개"). 60초에 여덟은 M4가 잰 손 속도(탭당 2.2초로 12개) 안이고, 넷이 앉으면
   * 32개짜리 방이 된다 - 프리셋 목표(구획당 20개)의 절반쯤이라 나머지는 하한 채움이 맡는다
   * (`matchDecorate.fillToFloor`). "사람이 놓은 것이 눈에 띄게 남는가"가 이 값의 판정 기준이고 플레이테스트 대상이다.
   */
  placementsPerPlayer: 8,
  /**
   * 미발견 하나의 값. 발견 하나가 술래에게 100점(`hunt.ts`)이므로 그 두 배로 잡았다 -
   * 체인저는 한 사람이고 술래는 여럿이라, 1:1이면 체인저가 이길 수 있는 판이 없다.
   * 컨셉시트 4.5절이 "숨긴 것이 안 들키면 득점"으로만 적어 둔 것을 숫자로 굳힌 값이며, 플레이테스트 대상이다.
   */
  changerPointsPerUnfound: 200,
  /**
   * 상태모델 12절의 "동시 클릭"을 피드백으로 바꾸는 창. 이 안이면 "OO이 0.4초 먼저"가 간다.
   * 손해가 0인 near-miss라 창을 넓혀도 규칙은 안 바뀐다 - 너무 넓으면 "먼저"가 거짓말이 될 뿐이다.
   */
  snipedWindowSeconds: 1.5,
  /**
   * 차례가 온 사람에게 주는 유예. **자동 워프는 없다**(컨셉시트 4.4 - 사람이 눌러야 들어간다).
   * 5초는 "지금 보던 곳을 마무리하고 누른다"에 충분하고, 뒤에 선 사람이 기다리기에는 짧다.
   */
  doorReserveSeconds: 5,
  /**
   * 60초 - 새로고침·터널·잠금 화면 복귀에 넉넉하고, 5분 수색의 1/5이라 "돌아오지 않는 사람"을 라운드 안에 알 수 있다.
   * 로드맵 M6 규칙 2의 값이다.
   */
  reconnectGraceSeconds: 60,
  /** 상태모델 9.1절의 N(판단 간격). 6초면 5분 수색에 50번 판단한다. */
  botIntervalSeconds: 6,
  /** 9.1절의 p. 50번 판단에 기대 발견 17.5개 → 12개를 5분 안에 다 찾는 "보통" 봇이다. */
  botFindChance: 0.35,
  /** 9.1절의 q. 50번 판단에 오답 12.5발 → 16발 안에서 마르지 않는다. 사람과 같은 자원 규칙이다. */
  botMissChance: 0.25,
  /** 원래 방 요청 확률. 문을 두고 줄이 서는 상황이 봇 판에서도 나와야 정원 1 규칙을 볼 수 있다. */
  botOriginChance: 0.1,
}

/* --- URL 쿼리 덮어쓰기 --------------------------------------------------------- */

/** 동사 목록 쿼리의 구분자. */
const VERB_SEPARATOR = ','

/** 유효한 동사 집합. `core/types.ts`의 `Verb` 유니온을 런타임에서 검사하기 위한 사본이다. */
const VALID_VERBS: ReadonlySet<string> = new Set<Verb>([
  'add',
  'remove',
  'move',
  'rotate',
  'recolor',
])

/** 숫자 키 하나의 파싱 규격. 키마다 검사를 손으로 적으면 반드시 하나가 빠진다. */
type NumericSpec = Readonly<{
  /** `Tuning`의 필드명. */
  key: Exclude<keyof TuningValues, 'allowedVerbs'>
  /** URL 쿼리 파라미터명. 짧게 - 폰에서 손으로 치는 값이다. */
  query: string
  /** 정수만 허용하는가(개수 계열). 초 단위 값은 소수를 허용한다. */
  isInteger: boolean
  min: number
  max: number
}>

/**
 * 숫자 튜닝의 허용 범위.
 *
 * 상한이 있는 이유는 오타 방어다 - `?hunt=3000`(50분)을 실수로 치면 라운드가 끝나지 않아
 * "게임이 멈췄다"로 보이고, 원인을 찾는 데 쓰는 시간이 곧 플레이테스트 시간이다.
 */
const NUMERIC_SPECS: readonly NumericSpec[] = [
  { key: 'changeCount', query: 'changes', isInteger: true, min: 1, max: 200 },
  { key: 'startAmmo', query: 'ammo', isInteger: true, min: 1, max: 99 },
  { key: 'ammoPerMagazine', query: 'magAmmo', isInteger: true, min: 1, max: 99 },
  // 0을 허용한다 - "탄창을 못 쓰는 라운드"가 밸런스 대전제("탄창 없이 클리어")의 대조군이다.
  { key: 'maxMagazinesPerRound', query: 'magRound', isInteger: true, min: 0, max: 20 },
  { key: 'watchSeconds', query: 'watch', isInteger: false, min: 1, max: 600 },
  { key: 'huntSeconds', query: 'hunt', isInteger: false, min: 5, max: 1800 },
  { key: 'revealSecondsPerChange', query: 'reveal', isInteger: false, min: 0.1, max: 30 },
  { key: 'originCooldownSeconds', query: 'cooldown', isInteger: false, min: 0, max: 600 },
  { key: 'originStaySeconds', query: 'stay', isInteger: false, min: 1, max: 600 },
  { key: 'warpDissolveSeconds', query: 'dissolve', isInteger: false, min: 0, max: 5 },
  { key: 'lowAmmoWarningAt', query: 'lowammo', isInteger: true, min: 0, max: 99 },
  // 0을 허용한다 - "잔탄과 무관하게 걸 수 있는 판"이 최소 잔탄 규칙의 대조군이다.
  { key: 'raiseMinAmmo', query: 'raiseMin', isInteger: true, min: 0, max: 99 },
  // 1이 하한이다 - 0이면 걸기 뒤 빗나감이 공짜가 되어 탄환 경제(4.3절)가 통째로 사라진다.
  { key: 'raiseMissCost', query: 'raiseCost', isInteger: true, min: 1, max: 99 },
  // --- 매치 전용. 봇 판을 재빌드 없이 재는 손잡이다(`game/match/`) ---
  { key: 'decorateSeconds', query: 'decorate', isInteger: false, min: 1, max: 600 },
  // `change`가 아니라 `changeSec`인 이유: `?changes=`(이상한 곳 개수)와 한 글자 차이라 오타가 다른 손잡이를 돌린다 -
  // 둘 다 유효 범위 안이면 경고도 없이 3초짜리 바꾸기 페이즈가 된다.
  { key: 'changeSeconds', query: 'changeSec', isInteger: false, min: 1, max: 600 },
  // 하한 1 - 인당 0개면 "고칠 것이 없는 사람"이 생기고(M11 대전제 위반) 구획 하나가 통째로 원본으로 남는다.
  { key: 'changesPerPlayer', query: 'perPlayer', isInteger: true, min: 1, max: 50 },
  { key: 'placementsPerPlayer', query: 'placements', isInteger: true, min: 1, max: 50 },
  // 0을 허용한다 - "체인저는 점수를 못 얻는 판"이 체인저 점수 규칙의 대조군이다.
  { key: 'changerPointsPerUnfound', query: 'changerPts', isInteger: true, min: 0, max: 10000 },
  { key: 'snipedWindowSeconds', query: 'sniped', isInteger: false, min: 0, max: 30 },
  // 0을 허용한다 - 유예 0은 "차례가 오면 즉시 다음 사람"이고, 그 판에서 예약 규칙이 보인다.
  { key: 'doorReserveSeconds', query: 'reserve', isInteger: false, min: 0, max: 60 },
  // 0을 허용한다 - 유예 0은 "끊기면 끝"이고, 그 판에서 좌석이 남는 규칙(봇이 대체하지 않는다)이 따로 보인다.
  { key: 'reconnectGraceSeconds', query: 'grace', isInteger: false, min: 0, max: 600 },
  { key: 'botIntervalSeconds', query: 'botEvery', isInteger: false, min: 0.1, max: 60 },
  { key: 'botFindChance', query: 'botFind', isInteger: false, min: 0, max: 1 },
  { key: 'botMissChance', query: 'botMiss', isInteger: false, min: 0, max: 1 },
  { key: 'botOriginChance', query: 'botOrigin', isInteger: false, min: 0, max: 1 },
]

/** 동사 목록 쿼리 파라미터명. */
const VERBS_QUERY_KEY = 'verbs'

/**
 * 더 이상 존재하지 않는 쿼리 키.
 *
 * 조용히 무시하지 않는 이유가 이 표의 전부다. 개발자의 브라우저 북마크·메모에는
 * `?refill1=8` 같은 주소가 남아 있고, 그걸 그대로 열면 **값이 안 먹었는데 먹은 줄 알고**
 * 그 판의 체감을 기록하게 된다. 그 순간 그 판의 데이터는 통째로 거짓이 된다.
 * 알 수 없는 키(`?seed=`)와 달리 **한때 의미가 있었던 키**라서 특별 취급이 필요하다.
 */
const REMOVED_QUERY_KEYS: readonly Readonly<{ query: string; why: string }>[] = [
  {
    query: 'refill1',
    why: '정답 방 리필은 제거됐다. 탄환 보충은 계정 탄창이 맡는다(?mags·?magAmmo)',
  },
  {
    query: 'refill2',
    why: '정답 방 리필은 제거됐다. 탄환 보충은 계정 탄창이 맡는다(?mags·?magAmmo)',
  },
  {
    query: 'items',
    why: '방에 떠 있던 탄환 아이템은 제거됐다. 탄창은 계정 자원이다(?mags로 보유량을 강제한다)',
  },
  {
    query: 'itemAmmo',
    why: '방에 떠 있던 탄환 아이템은 제거됐다. 탄창 하나의 회복량은 ?magAmmo다',
  },
]

/** 보유 탄창 수를 강제하는 쿼리 파라미터명. */
const MAGAZINES_QUERY_KEY = 'mags'

/**
 * 잘못된 쿼리 값을 알린다.
 *
 * 던지지 않는 이유: 오타 하나로 화면이 통째로 안 뜨면 플레이테스트가 멈춘다.
 * 조용히 삼키지도 않는 이유: 값이 안 먹었는데 먹은 줄 알고 판단하면 그 판의 데이터가 통째로 거짓이 된다.
 * 그래서 **무시하되 반드시 남긴다.**
 */
function warnInvalid(query: string, raw: string, why: string): void {
  console.warn(`[tuning] ?${query}=${raw} 무시됨 - ${why}`)
}

function parseNumeric(spec: NumericSpec, raw: string): number | undefined {
  const value = Number(raw)

  if (raw.trim() === '' || !Number.isFinite(value)) {
    warnInvalid(spec.query, raw, '숫자가 아니다')
    return undefined
  }
  if (spec.isInteger && !Number.isInteger(value)) {
    warnInvalid(spec.query, raw, '정수여야 한다')
    return undefined
  }
  if (value < spec.min || value > spec.max) {
    warnInvalid(spec.query, raw, `허용 범위는 ${spec.min}~${spec.max}`)
    return undefined
  }

  return value
}

function parseVerbs(raw: string): readonly Verb[] | undefined {
  const parts = raw
    .split(VERB_SEPARATOR)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part !== '')

  const verbs: Verb[] = []
  for (const part of parts) {
    if (!VALID_VERBS.has(part)) {
      warnInvalid(VERBS_QUERY_KEY, raw, `'${part}'은 동사가 아니다`)
      return undefined
    }
    // 중복은 오타지 의도가 아니다. 조용히 접는다(집합 의미이므로 결과가 달라지지 않는다).
    if (!verbs.includes(part as Verb)) verbs.push(part as Verb)
  }

  if (verbs.length === 0) {
    warnInvalid(VERBS_QUERY_KEY, raw, '동사가 하나도 없으면 변화를 만들 수 없다')
    return undefined
  }

  return verbs
}

/**
 * URL 쿼리에서 튜닝 덮어쓰기를 읽는다.
 *
 * 예: `?changes=8&ammo=10&magAmmo=4&magRound=1&hunt=180&verbs=add,remove`
 * 알 수 없는 키는 그냥 무시한다 - `?seed=...`처럼 다른 용도의 파라미터가 같은 쿼리에 섞여 있다.
 * 다만 **제거된 키**는 무시하되 경고한다(`REMOVED_QUERY_KEYS`).
 */
export function parseTuningOverrides(search: string): Partial<Tuning> {
  const params = new URLSearchParams(search)
  const overrides: Partial<TuningValues> = {}

  for (const removed of REMOVED_QUERY_KEYS) {
    const raw = params.get(removed.query)
    if (raw !== null) warnInvalid(removed.query, raw, removed.why)
  }

  for (const spec of NUMERIC_SPECS) {
    const raw = params.get(spec.query)
    if (raw === null) continue

    const value = parseNumeric(spec, raw)
    if (value !== undefined) overrides[spec.key] = value
  }

  const rawVerbs = params.get(VERBS_QUERY_KEY)
  if (rawVerbs !== null) {
    const verbs = parseVerbs(rawVerbs)
    if (verbs !== undefined) overrides.allowedVerbs = verbs
  }

  return overrides
}

/**
 * `?mags=N` - 이번 세션의 보유 탄창 수를 강제한다.
 *
 * **`Tuning`에 넣지 않은 이유**가 이 함수의 존재 이유다. 탄창은 라운드 규칙이 아니라
 * 계정 저장(`progress.ts`)에 사는 값이고, `Tuning`에 섞으면 "저장된 값과 튜닝 값 중
 * 무엇이 진실인가"라는 질문이 생긴다. 그래도 파싱만은 여기 두는데, 잘못된 값을 알리는 규약
 * (무시하되 반드시 남긴다)이 이 파일에 있고 그것을 두 벌 만들 이유가 없기 때문이다.
 *
 * 0을 허용한다 - **"탄창이 하나도 없는 판"이 이 개편의 가장 중요한 대조군**이다.
 * 밸런스 대전제가 "탄창 없이도 모든 단계를 클리어할 수 있다"이므로, 그것을 실제로
 * 확인할 수 있는 스위치가 없으면 대전제는 주장으로만 남는다.
 */
export function parseMagazineOverride(search: string): number | undefined {
  const raw = new URLSearchParams(search).get(MAGAZINES_QUERY_KEY)
  if (raw === null) return undefined

  const value = Number(raw)
  if (raw.trim() === '' || !Number.isFinite(value)) {
    warnInvalid(MAGAZINES_QUERY_KEY, raw, '숫자가 아니다')
    return undefined
  }
  if (!Number.isInteger(value)) {
    warnInvalid(MAGAZINES_QUERY_KEY, raw, '정수여야 한다')
    return undefined
  }
  if (value < 0 || value > MAX_MAGAZINE_COUNT) {
    warnInvalid(MAGAZINES_QUERY_KEY, raw, `허용 범위는 0~${String(MAX_MAGAZINE_COUNT)}`)
    return undefined
  }

  return value
}

/**
 * 기본 튜닝에 덮어쓰기를 얹은 최종 튜닝. 앱 부트스트랩이 부르는 입구다.
 *
 * 쿨타임을 기본값보다 낮추면 따로 경고한다. 기본값이 30초에서 5초로 내려온 지금은
 * 남은 여유가 거의 없다 - 여기서 더 내리면 깜빡비교(원본↔변경본 고속 토글)가 열려
 * 라운드가 몇 초 만에 끝나고, 그건 규칙의 재미가 아니라 규칙의 부재다.
 * 그런 판의 결과를 "재밌었다/없었다"로 읽으면 안 되므로 무엇이 달라졌는지 남긴다.
 */
export function resolveTuning(search: string, base: Tuning = TUNING): Tuning {
  const merged: Tuning = { ...base, ...parseTuningOverrides(search) }

  if (merged.originCooldownSeconds < base.originCooldownSeconds) {
    console.warn(
      `[tuning] 정답 방 쿨타임을 ${base.originCooldownSeconds}초 → ${merged.originCooldownSeconds}초로 낮췄다. ` +
        '깜빡비교(원본↔변경본 고속 토글)가 열리므로 이 판의 난이도는 정상 규칙의 것이 아니다.',
    )
  }

  return merged
}
